import { useCallback, useContext, useState } from 'react'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import GeoContext from 'contexts/geoContext'
import { createShareCard, shareLeWrapImage } from 'lib/image/captureElementAsImage'
import { getImageUrl } from 'lib/image/imageUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { PERSONALITY_IMAGE_IDS, PERSONALITY_NAMES } from '../Components/Slides/TravelPersonalityStyles'
import { useAppSelector } from 'hooks/reduxHooks'
import useDistanceDisplay from 'hooks/useDistanceDisplay'

interface UseWrappedShareResult {
  handleShare: () => Promise<void>
  isCapturing: boolean
}

function useWrappedShare(): UseWrappedShareResult {
  // Use granular selector to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const geo = useContext(GeoContext)
  const [isCapturing, setIsCapturing] = useState(false)

  const firstName = useAppSelector(state => state.auth.account.givenName)

  const { displayDistance, unitLabel } = useDistanceDisplay(data?.stats?.totalDistanceKm ?? 0)

  const handleShare = useCallback(async() => {
    if (!data || isCapturing) return

    setIsCapturing(true)

    try {
      const { stats, personality, topDestinations, loyalty, topOffer } = data
      const isLowDataMode = stats.citiesVisited.length === 0

      // Get the personality image URL for the share card
      const personalityImgUrl = getImageUrl(PERSONALITY_IMAGE_IDS[personality.type], {
        format: 'png',
        width: 120,
      })

      // Create a shareable image card
      const blob = await createShareCard({
        firstName,
        personalityName: PERSONALITY_NAMES[personality.type],
        personalityImageUrl: personalityImgUrl,
        stats: {
          citiesVisited: stats.citiesVisited.length,
          countriesVisited: stats.countriesVisited,
          totalNights: stats.totalNights,
          totalDistanceUnit: unitLabel,
          totalDistanceValue: displayDistance,
          totalSavings: stats.totalSavings,
          currency: stats.currency,
          regionCode: geo.currentRegionCode,
          offersViewed: stats.offersViewed,
        },
        topDestination: topDestinations[0]?.name,
        isLowData: isLowDataMode,
        loyalty: loyalty ? {
          points: loyalty.points,
          statusCredits: loyalty.statusCredits,
          percentile: loyalty.percentile,
          tier: loyalty.tier,
        } : undefined,
        topOffer: isLowDataMode && topOffer?.propertyName ? {
          propertyName: topOffer.propertyName,
          inclusions: topOffer.inclusions.map(inc => ({ description: inc.description })),
        } : undefined,
      })

      // Share the image using native share or open in new tab
      await shareLeWrapImage(blob, {
        fileName: 'year-of-escapes-2025.png',
      })
    } catch(error) {
      console.error('Failed to create and share image:', error)
      showSnackbar('Unable to share image. Please try again.', 'neutral')
    } finally {
      setIsCapturing(false)
    }
  }, [data, isCapturing, firstName, unitLabel, displayDistance, geo.currentRegionCode])

  return { handleShare, isCapturing }
}

export default useWrappedShare
